/**
 * The message step of a WhatsApp campaign.
 *
 * Nothing is written here, because nothing can be: a business may send free
 * text only inside the 24 hours after somebody last messaged it, and a
 * campaign is business-initiated by definition. So a WhatsApp campaign *is*
 * one of the templates Meta has already approved, and this step is about
 * choosing which, and saying what goes into its blanks.
 *
 * A template body reads "Hi {{1}}, your {{2}} renews on {{3}}". Each blank is
 * mapped to a merge field — or to fixed words — and the preview shows the
 * message as one real contact would receive it, in the shape they would see
 * it in: a bubble, with the buttons under it.
 *
 * The plain-text body the rest of the app carries (the send console, the
 * export, the review) is derived from the mapping and kept on the campaign,
 * so every other screen sees the message without knowing what a template is.
 */
import { useEffect, useMemo, useState } from 'react';
import api from '../../lib/api.js';
import { isOffline } from '../../lib/storage.js';
import Icon from '../../lib/icons.jsx';
import { Spinner } from '../Primitives.jsx';
import { fileToBase64 } from '../../lib/format.js';
import { Field } from '../Sheet.jsx';
import { applyMerge } from '../../domain/marketing.js';
import { MERGE, LEDGER_MERGE } from '../../constants/marketing.js';

/** The choices for one blank: the merge fields, or words typed in. */
const CUSTOM = '__custom__';

/** {{1}} … {{n}}, in order, from a template body. */
export const slotsIn = text => {
  const found = new Set((String(text || '').match(/\{\{(\d+)\}\}/g) || []));
  return [...found].sort((a, b) => Number(a.slice(2, -2)) - Number(b.slice(2, -2)));
};

/** The body with each {{n}} replaced by what was chosen for it. */
export const fillTemplate = (body, fields) =>
  String(body || '').replace(/\{\{(\d+)\}\}/g, (m, n) => {
    const v = (fields || [])[Number(n) - 1];
    return v == null || v === '' ? m : v;
  });

export default function WhatsAppStep({ form, set, sample, S }) {
  const [list, setList] = useState(null);
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');

  useEffect(() => {
    if (isOffline()) { setList([]); return; }
    api.whatsapp.templates()
      .then(out => setList(out.templates || []))
      .catch(e => { setError(e.message); setList([]); });
  }, []);

  const chosen = useMemo(
    () => (list || []).find(t => t.name === form.waTemplate && (!form.waTemplateLang || t.language === form.waTemplateLang))
      || (list || []).find(t => t.name === form.waTemplate)
      || null,
    [list, form.waTemplate, form.waTemplateLang]
  );

  const body = form.waBody || (chosen && chosen.body) || '';
  const slots = slotsIn(body);
  const fields = form.waFields || [];

  /*
   * Everything the rest of the app needs is written onto the campaign the
   * moment a template is picked: its name and language for the send, its
   * body for the preview, and a plain-text `body` with the blanks replaced by
   * merge fields so the console and the export show a real message.
   */
  const pick = name => {
    const t = (list || []).find(x => x.name === name);
    if (!t) {
      set('waTemplate', '');
      set('waBody', '');
      set('waFields', []);
      set('body', '');
      return;
    }

    const blanks = slotsIn(t.body);
    // A sensible first guess: the first blank is nearly always the name.
    const guess = blanks.map((_, i) => (i === 0 ? '{{first}}' : ''));

    set('waTemplate', t.name);
    set('waTemplateLang', t.language);
    set('waTemplateVars', blanks.length);
    set('waBody', t.body);
    set('waButtons', t.buttons || []);
    set('waHeader', t.header || null);
    // A different template may want a different picture, or none.
    set('waHeaderUrl', '');
    set('waFooter', t.footer || '');
    set('waFields', guess);
    set('body', fillTemplate(t.body, guess));
  };

  const setField = (i, value) => {
    const next = slots.map((_, k) => (k === i ? value : (fields[k] || '')));
    set('waFields', next);
    set('body', fillTemplate(body, next));
  };

  /*
   * The picture for a template with an image on top.
   *
   * Meta fetches it from a URL at send time, so it has to live somewhere a
   * stranger's server can reach. The workspace's own store is that place -
   * the upload comes back with a public address - or any address already on
   * the web will do.
   */
  const upload = async file => {
    if (!file) return;
    setUploading(true);
    setUploadError('');
    try {
      const asset = await api.marketing.asset({
        name: file.name, mime: file.type, data: await fileToBase64(file)
      });
      if (!asset.url) {
        throw new Error('This server has no public address (PUBLIC_URL), so Meta could not fetch it.');
      }
      set('waHeaderUrl', asset.url);
    } catch (e) {
      setUploadError(e.message);
    }
    setUploading(false);
  };

  /* Shown as the sample contact would receive it. With nobody picked yet
     the blanks show their merge field, which is still worth seeing. */
  const shown = sample ? applyMerge(fillTemplate(body, fields), sample) : fillTemplate(body, fields);
  const buttons = form.waButtons || (chosen && chosen.buttons) || [];
  const footer = form.waFooter || (chosen && chosen.footer) || '';
  const header = form.waHeader || (chosen && chosen.header) || null;

  if (list === null) {
    return (
      <Field label="Template" wide>
        <p className="muted"><Spinner /> Loading your approved templates…</p>
      </Field>
    );
  }

  if (!list.length) {
    return (
      <Field label="Template" wide>
        <div className="fhint" style={{ margin: 0 }}>
          {error
            ? `Could not read the template list — ${error}`
            : 'No approved templates could be read. Connect Meta in Campaigns → Channel setup → WhatsApp, or approve a template in Chatlio first.'}
        </div>
      </Field>
    );
  }

  return (
    <>
      <Field
        label="Template"
        wide
        hint={
          <div className="fhint">
            Approved on Meta and shown in Chatlio. The wording is fixed; only the blanks
            take values.
          </div>
        }
      >
        <select value={form.waTemplate || ''} onChange={e => pick(e.target.value)}>
          <option value="">Choose a template</option>
          {list.map(t => (
            <option key={`${t.name}:${t.language}`} value={t.name}>
              {t.name} · {t.language}{t.vars ? ` · ${t.vars} blank${t.vars > 1 ? 's' : ''}` : ''}
            </option>
          ))}
        </select>
      </Field>

      {chosen || form.waBody ? (
        <div className="wastep">
          {/* ------------------------------------------------ the blanks */}
          {slots.length ? (
            <div className="wablanks">
              <label>What goes in the blanks</label>
              {slots.map((slot, i) => {
                const value = fields[i] || '';
                const isMerge = MERGE.some(([token]) => token === value);
                const mode = !value ? '' : isMerge ? value : CUSTOM;

                return (
                  <div className="wablank" key={slot}>
                    <code>{slot}</code>
                    <span>→</span>
                    <select
                      value={mode}
                      onChange={e => setField(i, e.target.value === CUSTOM ? ' ' : e.target.value)}
                    >
                      <option value="">Choose…</option>
                      {MERGE.map(([token, description]) => (
                        <option key={token} value={token}>
                          {token}{LEDGER_MERGE.includes(token) ? ' (clients only)' : ''} — {description}
                        </option>
                      ))}
                      <option value={CUSTOM}>Fixed words…</option>
                    </select>
                    {mode === CUSTOM ? (
                      <input
                        value={value === ' ' ? '' : value}
                        placeholder="the same words for everyone"
                        onChange={e => setField(i, e.target.value || ' ')}
                      />
                    ) : null}
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="fhint">This template has no blanks — everyone receives the same words.</p>
          )}

          {/* ------------------------------------------- the picture */}
          {header && header.format && header.format !== 'text' ? (
            <div className="wablanks">
              <label>The {header.format} on top</label>
              <div className="wablank">
                <input
                  value={form.waHeaderUrl || ''}
                  placeholder={`Address of the ${header.format} Meta will fetch — or upload one`}
                  onChange={e => set('waHeaderUrl', e.target.value.trim())}
                  style={{ flex: '1 1 320px' }}
                />
                <label className="btn sm" style={{ cursor: 'pointer' }}>
                  {uploading ? <><Spinner />Uploading…</> : <><Icon name="up" />Upload</>}
                  <input
                    type="file"
                    hidden
                    accept={header.format === 'image' ? 'image/png,image/jpeg' : header.format === 'video' ? 'video/mp4' : 'application/pdf'}
                    onChange={e => { upload(e.target.files && e.target.files[0]); e.target.value = ''; }}
                  />
                </label>
              </div>
              {uploadError ? (
                <div className="fhint" style={{ color: 'var(--red)', margin: 0 }}>{uploadError}</div>
              ) : null}
              {!form.waHeaderUrl ? (
                <div className="fhint" style={{ color: 'var(--ch-sms)', margin: 0 }}>
                  This template has {header.format === 'image' ? 'an image' : `a ${header.format}`} on
                  top and Meta will refuse to send it without one.
                </div>
              ) : null}
            </div>
          ) : null}

          {/* ----------------------------------------------- the preview */}
          <label className="walabel">
            {sample ? `As ${sample.name} would receive it` : 'How it will read'}
          </label>
          <div className="wachat">
            <div className="wabubble">
              {header && header.format === 'text' && header.text ? (
                <div className="wahead">{sample ? applyMerge(header.text, sample) : header.text}</div>
              ) : header && header.format === 'image' && form.waHeaderUrl ? (
                <img className="waimg" src={form.waHeaderUrl} alt="" />
              ) : header && header.format && header.format !== 'text' ? (
                <div className="wamedia"><Icon name="file" />{header.format}{form.waHeaderUrl ? ' attached' : ' — none yet'}</div>
              ) : null}

              <div className="wabody">{shown}</div>

              {footer ? <div className="wafoot">{footer}</div> : null}
              <div className="watime">{new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</div>
            </div>

            {buttons.length ? (
              <div className="wabtns">
                {buttons.map((b, i) => (
                  <div className="wabtn" key={i}>
                    <Icon name={b.type === 'url' ? 'out' : b.type === 'phone_number' ? 'phone' : 'chat'} />
                    {b.text}
                  </div>
                ))}
              </div>
            ) : null}
          </div>

          {slots.some((_, i) => !(fields[i] || '').trim()) ? (
            <div className="fhint" style={{ color: 'var(--ch-sms)' }}>
              A blank left empty goes out as “{slots.find((_, i) => !(fields[i] || '').trim())}” — Meta
              will refuse it. Fill every blank.
            </div>
          ) : null}
        </div>
      ) : null}
    </>
  );
}
